import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  costOf,
  esCuentaEnDolares,
  formatFxRate,
  formatMoney,
  formatQuantity,
  fxRate,
  hoy,
  newId,
  parseMoney,
  parseQuantity,
  saleToBase,
  unitCostFromTotal,
  usdToCordobas,
  type Centavos,
  type CuentaBanco,
  type CuentaBancoCordobas,
  type Milli,
} from '@abastece/shared';
import type { FormaPago, Producto, UnidadVenta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { cuentaEnCordobas, cuentaQueQueda } from '../lib/bancos.js';
import { unidadesDe } from '../lib/catalogo.js';
import { emitirYVer } from '../lib/emitir.js';
import { anunciar } from '../lib/hecho.js';
import { pagoDeLaCompra, restoDeLaCompra } from '../lib/pagos-de-compra.js';
import { usePuede } from '../lib/sesion.js';
import { volver } from '../lib/volver.js';
import { Campo, Selector } from '../ui/Campo.js';
import { ElegirCuenta } from '../ui/ElegirCuenta.js';
import { ElegirEscribiendo } from '../ui/ElegirEscribiendo.js';
import { BuscadorProducto } from '../ui/BuscadorProducto.js';
import { enMoneda } from '../ui/ContarBilletes.js';
import { FormularioProducto } from '../ui/FormularioProducto.js';
import { FormularioProveedor } from '../ui/FormularioProveedor.js';
import { Modal } from '../ui/Modal.js';
import { Pagina } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { useT, type Clave } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Miniatura } from '../ui/Miniatura.js';
import { PartesDelPago, type ParteEscrita } from '../ui/PartesDelPago.js';
import { SaleDeLaGaveta, faltaEnGaveta, useGaveta } from '../ui/SaleDeLaGaveta.js';
import { PreciosBajoMargen, buscarPreciosBajos } from '../ui/PreciosBajoMargen.js';
import { DESVIO_AVISO, compraRepetida, costoConocido, desvio, enRango } from '../lib/costos.js';
import { leerTasa } from '../lib/configuracion.js';

interface Linea {
  key: string;
  producto: Producto;
  unidadId: string;
  cantidad: string;
  costoTotal: string;
  lote: string;
  vence: string;
}

export function NuevaCompra() {
  const t = useT();
  const nombre = useNombreProducto();
  const proveedores = useLiveQuery(() => db.proveedores.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], null);
  /** What each product has been costing, and the invoices already entered: to check this one against. */
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);
  const compras = useLiveQuery(() => db.compras.toArray(), [], []);

  const activos = proveedores.filter((p) => p.activo);

  const [proveedorId, setProveedorId] = useState('');
  const [fecha, setFecha] = useState(hoy);
  const [documento, setDocumento] = useState('');
  // One place for the goods, the shop: «we have just one location» (the owner, 2026-10-05).
  const ubicacion = 'tienda';
  const [moneda, setMoneda] = useState<'NIO' | 'USD'>('NIO');
  const [tasa, setTasa] = useState('');
  const [formaPago, setFormaPago] = useState<FormaPago>('efectivo_cordobas');
  /** The bank account «Transferencia» pays from, once picked: a dollar account only for a purchase in dollars. */
  const [cuentaElegida, setCuentaElegida] = useState<CuentaBanco | null>(null);
  const [vencePago, setVencePago] = useState('');
  const [flete, setFlete] = useState('');
  /** Blank is none; typed and unreadable, or below nothing, null: said, never booked as no freight. */
  const fleteEscrito = flete.trim() === '' ? 0 : ((x) => (x !== null && x >= 0 ? x : null))(parseMoney(flete));
  /** The invoice's IVA, when it has any (the owner, 2026-10-11): part of what the goods cost, as freight. */
  const [iva, setIva] = useState('');
  const ivaEscrito = iva.trim() === '' ? 0 : parseMoney(iva);
  /** Parts paid some other way: «Forma de pago» pays what they leave (the owner, 2026-10-04). */
  const [partes, setPartes] = useState<ParteEscrita[]>([]);
  /** The part just added, whose amount takes the cursor. */
  const [enfocar, setEnfocar] = useState<string | null>(null);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  /**
   * A supplier not on the list yet, made in a sheet over the purchase (the owner, 2026-10-04), with
   * what was typed in the field for its name: null while there is no sheet.
   */
  const [proveedorNuevo, setProveedorNuevo] = useState<string | null>(null);
  const puedeAgregarProveedor = usePuede('recibir_compras');
  /** This supplier's same invoice, already entered: warned, since the second time gets paid twice. */
  const repetida = proveedorId ? compraRepetida(compras, proveedorId, documento) : undefined;
  /** The products this purchase left priced under the margin floor, shown before leaving. */
  const [revisando, setRevisando] = useState<string[] | null>(null);
  /** Something that came in and is not in the catalogue, being made a product: its code, or its name. */
  const [nuevo, setNuevo] = useState<{ codigo?: string; nombre?: string } | null>(null);
  const puedeCrear = usePuede('editar_productos');
  /**
   * What a product has been costing is the books' average, and the margins a purchase leaves are
   * worked from it: both the owner's to see. A cashier records purchases too since 2026-10-09, and
   * is still warned when a cost is far off, without the figure.
   */
  const verCostos = usePuede('ver_costos');

  /** The purchase in its own currency, which the parts are typed in. */
  const totalMoneda =
    lineas.reduce((a, l) => a + (parseMoney(l.costoTotal) ?? 0), 0) + (fleteEscrito ?? 0) + (ivaEscrito ?? 0);
  const montosPartes = partes.map((x) => parseMoney(x.monto));
  const resto = restoDeLaCompra(totalMoneda, montosPartes.map((x) => ({ monto: x ?? 0 })));
  /** Whether «Forma de pago» pays anything: always without parts, and while they leave something. */
  const pagaElResto = partes.length === 0 || resto > 0;
  const ajustes = config ?? DEFAULT_SETTINGS;
  const cuentaPago = cuentaQueQueda(ajustes, cuentaElegida, moneda);
  const cuentaParte = (x: ParteEscrita): CuentaBancoCordobas => cuentaEnCordobas(ajustes, x.cuenta);
  /** The rest paid by transfer out of a dollar account: its dollars, at the official rate, as dollars from the safe. */
  const deCuentaEnDolares = moneda === 'USD' && pagaElResto && formaPago === 'transferencia' && esCuentaEnDolares(cuentaPago);

  /**
   * Paid in dollars out of general cash, the dollars leave it at the official rate, which is
   * what they are carried at there, and a part in dollars takes the whole purchase with it. Any
   * other way, the rate typed: what the córdobas came to.
   */
  const conDolares =
    deCuentaEnDolares ||
    (moneda === 'USD' &&
      ((pagaElResto && formaPago === 'efectivo_dolares') || partes.some((x) => x.formaPago === 'efectivo_dolares')));
  // Read as the rate is everywhere (`leerTasa`): «36,62» is 36.62, where it used to read as 36 even.
  const tasaLeida = leerTasa(tasa);
  const tasaUsada = moneda !== 'USD' ? 0 : conDolares ? (config?.fxTasaOficial ?? 0) : tasaLeida.ok ? tasaLeida.valor : 0;

  const totales = useMemo(() => {
    const sub = lineas.reduce((a, l) => a + (parseMoney(l.costoTotal) ?? 0), 0);
    const f = fleteEscrito ?? 0;
    const i = ivaEscrito ?? 0;
    const total = sub + f + i;
    // Null past what the books take, as while a code scanned into a dollar cost is still there.
    const enCordobas =
      moneda === 'USD' && tasaUsada > 0
        ? enRango(() => usdToCordobas(total as Centavos, fxRate(Math.round(tasaUsada))))
        : (total as Centavos);
    return { sub: sub as Centavos, flete: f as Centavos, iva: i as Centavos, total: total as Centavos, enCordobas };
  }, [lineas, fleteEscrito, ivaEscrito, moneda, tasaUsada]);

  /**
   * Córdobas in cash, which the drawer pays when it is on: every part paid that way, and what is
   * left when «Forma de pago» is too. One switch for all of it, in córdobas.
   */
  const efectivoCordobas = [
    ...partes.flatMap((x, i) => (x.formaPago === 'efectivo_cordobas' ? [montosPartes[i] ?? 0] : [])),
    ...(pagaElResto && formaPago === 'efectivo_cordobas' ? [resto] : []),
  ];
  // Off as it comes on a purchase (the owner, 2026-10-12): turned on, the drawer pays it.
  // Only what is paid today comes out of today's drawer (`PasosDeGasto`'s rule).
  const gaveta = useGaveta(efectivoCordobas.length > 0 && fecha === hoy() ? 'efectivo_cordobas' : '', { encendidaAlEmpezar: false });
  const deLaGaveta = efectivoCordobas.reduce<number | null>((a, monto) => {
    const c =
      moneda !== 'USD' ? monto : tasaUsada > 0 ? enRango(() => usdToCordobas(monto as Centavos, fxRate(Math.round(tasaUsada)))) : null;
    return a === null || c === null ? null : a + c;
  }, 0);

  function agregarParte() {
    const pagoId = newId();
    setPartes((xs) => [...xs, { pagoId, formaPago: 'efectivo_cordobas', monto: '', cuenta: null }]);
    setEnfocar(pagoId);
  }
  function cambiarParte(pagoId: string, patch: Partial<ParteEscrita>) {
    setPartes((xs) => xs.map((x) => (x.pagoId === pagoId ? { ...x, ...patch } : x)));
  }

  /** A line for what was found, in the presentation whose own code was scanned: the case's code, cases. */
  function agregar(producto: Producto, unidad?: UnidadVenta) {
    const us = unidadesDe(unidades, producto.id);
    setLineas((l) => [
      ...l,
      {
        key: newId(),
        producto,
        unidadId: unidad?.id ?? us[0]?.id ?? '',
        cantidad: '',
        costoTotal: '',
        lote: '',
        vence: '',
      },
    ]);
  }

  /** The product just made in the sheet, onto the purchase. */
  async function creado({ productoId, unidadVentaId }: { productoId: string; unidadVentaId: string }) {
    const [producto, unidad] = await Promise.all([db.productos.get(productoId), db.unidades.get(unidadVentaId)]);
    if (producto) agregar(producto, unidad);
    setNuevo(null);
  }

  function actualizar(key: string, patch: Partial<Linea>) {
    setLineas((l) => l.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  }

  function cantidadBaseDe(l: Linea): Milli | null {
    const q = parseQuantity(l.cantidad);
    if (q === null || q <= 0) return null;
    const unidad = unidades.find((u) => u.id === l.unidadId);
    return unidad ? saleToBase(q, unidad.factor) : q;
  }

  function validar(): string | null {
    if (!proveedorId) return t('compra.elijaProveedor');
    if (fecha > hoy()) return t('gastoPaso.fechaFutura');
    if (lineas.length === 0) return t('compra.agregueProducto');
    if (moneda === 'USD' && !conDolares && tasa.trim() !== '' && !tasaLeida.ok) return t('compra.tasaInvalida');
    if (moneda === 'USD' && !(tasaUsada > 0)) return t('compra.faltaTasa');
    if (fleteEscrito === null) return t('compra.fleteInvalido');
    if (montosPartes.some((x) => x === null || x <= 0)) return t('compra.faltaMontoParte');
    if (ivaEscrito === null) return t('compra.ivaInvalido');
    if (resto < 0) return t('compra.partesDeMas', { monto: enMoneda(-resto as Centavos, moneda) });
    if (pagaElResto && formaPago === 'credito' && !vencePago) return t('compra.faltaVencimiento');
    if (totales.enCordobas === null || deLaGaveta === null) return t('compra.demasiado');
    const sinGaveta = faltaEnGaveta(gaveta, deLaGaveta, t);
    if (sinGaveta) return sinGaveta;

    for (const l of lineas) {
      const producto = nombre(l.producto);
      if (cantidadBaseDe(l) === null) return t('compra.faltaCantidad', { producto });
      const costo = parseMoney(l.costoTotal);
      if (costo === null || costo < 0) return t('compra.faltaCosto', { producto });
      // Captured at receipt or lost forever: nobody can reconstruct an expiry date later.
      if ((l.producto.esMedicamento || l.producto.perecedero) && (!l.lote.trim() || !l.vence)) {
        return t('compra.faltaLote', { producto });
      }
    }
    return null;
  }

  async function guardar() {
    const problema = validar();
    if (problema) {
      setError(problema);
      return;
    }
    setGuardando(true);
    setError(null);

    try {
      const { rechazo } = await emitirYVer('compra.registrada', {
        compraId: newId(),
        proveedorId,
        fecha,
        ...(documento.trim() ? { documento: documento.trim() } : {}),
        moneda,
        ...(moneda === 'USD' ? { tasaCambio: Math.round(tasaUsada) } : {}),
        flete: fleteEscrito ?? 0,
        ...(ivaEscrito ? { iva: ivaEscrito } : {}),
        ubicacion,
        ...pagoDeLaCompra({
          total: totalMoneda,
          partes: partes.map((x, i) => ({
            pagoId: x.pagoId,
            formaPago: x.formaPago,
            monto: montosPartes[i]!,
            ...(x.formaPago === 'transferencia' ? { cuenta: cuentaParte(x) } : {}),
          })),
          formaPago,
          vencePago,
          sesionId: gaveta.sesionId,
          cuenta: cuentaPago,
        }),
        lineas: lineas.map((l) => ({
          productoId: l.producto.id,
          cantidadBase: cantidadBaseDe(l)!,
          costoTotal: parseMoney(l.costoTotal)!,
          ...(l.lote.trim() && l.vence
            ? { lote: { numero: l.lote.trim(), vencimiento: l.vence } }
            : {}),
        })),
      });
      // Refused (the drawer short by what another phone took meanwhile, say): said here, nothing claimed.
      if (rechazo) {
        setError(rechazo);
        setGuardando(false);
        return;
      }
      // Recorded: back where the purchase was begun, the home screen's «Mercadería comprada», the
      // purchases or wherever it was, saying what went in (the owner, 2026-10-08). A cost that
      // went up can leave list prices under the floor: said first, not at the next sale, to
      // whoever sees the margins. A cashier's purchase leaves that to the owner's home screen.
      anunciar(
        t('compra.registradaDe', {
          proveedor: proveedores.find((p) => p.id === proveedorId)?.nombre ?? '—',
          total: enMoneda(totalMoneda as Centavos, moneda),
        }),
      );
      const tocados = [...new Set(lineas.map((l) => l.producto.id))];
      if (verCostos && (await buscarPreciosBajos(tocados)).length > 0) setRevisando(tocados);
      else void volver({ to: '/compras' });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('compra.noSePudo'));
      setGuardando(false);
    }
  }

  return (
    <Pagina
      titulo={t('compra.nueva')}
      volverA="/compras"
      ancho="amplio"
      acciones={
        // The purchases already entered, by name: the home tile opens this form, and «Volver»
        // never said it led to them (the owner, 2026-10-05: «is there no way to see the history?»).
        <Link to="/compras" className="btn-secundario h-11 min-h-0 gap-1.5 px-3 text-sm" data-compras-anteriores>
          <Ico icono="compras" className="size-4" />
          {t('compras.anteriores')}
        </Link>
      }
    >
      {/*
        What came in, first: the goods are in hand when the truck is at the door, and each is
        scanned, or looked up by name, as it comes off. Who brought them and how they were paid
        is the invoice's, and follows.
      */}
      <section className="tarjeta mb-4">
        <div data-guia="compra-agregar">
          <BuscadorProducto
            onElegir={agregar}
            etiqueta={t('pedidos.agregarProducto')}
            // The scanner is how goods come in here: what it types is read wherever the cursor
            // was, and a code nobody has is a question that has to be answered.
            lectorUsb
            preguntarEnHoja
            onNuevo={puedeCrear ? (nombre) => setNuevo({ nombre }) : undefined}
            escaner={{
              // A code nobody has: a new product with it, beside picking the product it is on.
              desconocido: puedeCrear
                ? (codigo, cerrar) => (
                    <button
                      type="button"
                      className="btn-secundario"
                      onClick={() => {
                        cerrar();
                        setNuevo({ codigo });
                      }}
                    >
                      <Ico icono="nuevo" />
                      {t('productos.crearConCodigo')}
                    </button>
                  )
                : undefined,
            }}
          />
        </div>

        {lineas.length > 0 && (
          <ul className="mt-4 flex flex-col divide-y divide-slate-800">
            {lineas.map((l) => {
              const us = unidadesDe(unidades, l.producto.id);
              const base = cantidadBaseDe(l);
              const exigeLote = l.producto.esMedicamento || l.producto.perecedero;
              /*
               * What this line makes a unit cost, before freight, and how far that is from what the
               * product has been costing: a case typed as units puts the cost off by the pack size,
               * and that is the moment to catch it — once the average has moved it cannot be undone.
               */
              // Below nothing is no cost to show: it used to stop the whole screen.
              const costoLinea = ((x) => (x !== null && x >= 0 ? x : null))(parseMoney(l.costoTotal));
              const enCordobas =
                costoLinea === null || (moneda === 'USD' && tasaUsada <= 0)
                  ? null
                  : moneda === 'USD'
                    ? enRango(() => usdToCordobas(costoLinea, fxRate(Math.round(tasaUsada))))
                    : costoLinea;
              const porBase = base !== null && base > 0 && enCordobas !== null ? unitCostFromTotal(enCordobas, base) : null;
              const unidadElegida = us.find((u) => u.id === l.unidadId);
              // Each past what the books take while a code scanned into the cost is still there.
              const porUnidad = porBase === null ? null : enRango(() => costOf(porBase, 1000 as Milli));
              const porPresentacion =
                porBase === null || !unidadElegida || unidadElegida.factor === 1000
                  ? null
                  : enRango(() => costOf(porBase, unidadElegida.factor));
              const antes = costoConocido(l.producto.id, ubicacion, existencias, compras);
              const diferencia = porBase !== null ? desvio(porBase, antes) : null;
              const nombreBase = etiquetaUnidad(l.producto.unidadBase, l.producto.unidadBase);
              return (
                <li key={l.key} className="py-4" data-guia="compra-linea">
                  <div className="mb-2 flex items-center gap-3">
                    <Miniatura producto={l.producto} tamano="sm" />
                    <p className="min-w-0 flex-1 font-medium">{nombre(l.producto)}</p>
                    <button
                      type="button"
                      className="btn-texto shrink-0 gap-1 text-xs text-slate-500"
                      onClick={() => setLineas((xs) => xs.filter((x) => x.key !== l.key))}
                    >
                      <Ico icono="quitar" className="size-3.5" />
                      {t('comun.quitar')}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <Campo
                      etiqueta={t('comun.cantidad')}
                      inputMode="decimal"
                      className="tabular"
                      value={l.cantidad}
                      onChange={(e) => actualizar(l.key, { cantidad: e.target.value })}
                    />
                    <Selector
                      etiqueta={t('compra.unidad')}
                      value={l.unidadId}
                      onChange={(e) => actualizar(l.key, { unidadId: e.target.value })}
                    >
                      {us.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nombre}
                        </option>
                      ))}
                    </Selector>
                    <Campo
                      etiqueta={t('compra.costoTotal', {
                        moneda: moneda === 'USD' ? 'US$' : 'C$',
                      })}
                      inputMode="decimal"
                      className="tabular"
                      value={l.costoTotal}
                      onChange={(e) => actualizar(l.key, { costoTotal: e.target.value })}
                      ayuda={t('compra.ayudaCosto')}
                    />
                    <div className="flex items-end pb-1 text-xs text-slate-500">
                      {base !== null && (
                        <span className="tabular">
                          = {formatQuantity(base)}{' '}
                          {etiquetaUnidad(l.producto.unidadBase, l.producto.unidadBase)}
                        </span>
                      )}
                    </div>
                  </div>

                  {porUnidad !== null && (
                    <p className="tabular mt-2 text-xs text-slate-400">
                      {t('compra.costoPorUnidad', { monto: formatMoney(porUnidad), unidad: nombreBase })}
                      {unidadElegida && porPresentacion !== null && (
                        <>
                          {' · '}
                          {t('compra.costoPorUnidad', { monto: formatMoney(porPresentacion), unidad: unidadElegida.nombre })}
                        </>
                      )}
                    </p>
                  )}
                  {diferencia !== null && Math.abs(diferencia) > DESVIO_AVISO && (
                    <p className="mt-1 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                      {verCostos
                        ? t(diferencia > 0 ? 'compra.costoMasAlto' : 'compra.costoMasBajo', {
                            pct: Math.round(Math.abs(diferencia) * 100),
                            antes: formatMoney(costOf(antes, 1000 as Milli)),
                            unidad: nombreBase,
                          })
                        : t(diferencia > 0 ? 'compra.costoMasAltoSinMonto' : 'compra.costoMasBajoSinMonto')}
                    </p>
                  )}

                  {exigeLote && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <Campo
                        maxLength={40}
                        etiqueta={t('compra.lote')}
                        value={l.lote}
                        onChange={(e) => actualizar(l.key, { lote: e.target.value })}
                        autoComplete="off"
                      />
                      <Campo
                        etiqueta={t('compra.vence')}
                        type="date"
                        value={l.vence}
                        onChange={(e) => actualizar(l.key, { vence: e.target.value })}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="tarjeta mb-4 grid grid-cols-1 gap-4 md:grid-cols-4">
        <div data-guia="compra-proveedor">
        <ElegirEscribiendo
          etiqueta={t('compra.proveedor')}
          placeholder={t('elegir.proveedor')}
          opciones={activos}
          value={proveedorId}
          onChange={setProveedorId}
          // The sheet for a new one, at the list's foot; the supplier picked stays as it was until it is saved.
          nuevo={
            puedeAgregarProveedor
              ? {
                  etiqueta: (escrito) => (escrito ? t('compra.agregarComo', { nombre: escrito }) : t('compra.nuevoProveedor')),
                  onNuevo: setProveedorNuevo,
                }
              : undefined
          }
          datos="proveedor"
        />
        <Campo
          etiqueta={t('comun.fecha')}
          type="date"
          max={hoy()}
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
        />
        <Campo
          maxLength={60}
          etiqueta={t('compra.factura')}
          value={documento}
          onChange={(e) => setDocumento(e.target.value)}
          autoComplete="off"
          placeholder={t('compra.opcional')}
          error={
            repetida
              ? t('compra.facturaRepetida', { fecha: repetida.fecha, monto: formatMoney(repetida.totalCordobas) })
              : null
          }
        />
        </div>
      </section>

      <section className="tarjeta mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Selector
          etiqueta={t('gastos.moneda')}
          value={moneda}
          onChange={(e) => {
            const m = e.target.value as 'NIO' | 'USD';
            setMoneda(m);
            if (m === 'USD' && !tasa && config) setTasa(formatFxRate(config.fxTasaTienda));
          }}
        >
          <option value="NIO">{t('gastos.cordobas')}</option>
          <option value="USD">{t('gastos.dolares')}</option>
        </Selector>

        {moneda === 'USD' && (
          <Campo
            etiqueta={t('compra.tasaDelDia')}
            inputMode="decimal"
            className={`tabular ${conDolares ? 'text-slate-400' : ''}`}
            value={conDolares ? formatFxRate(tasaUsada as never) : tasa}
            onChange={(e) => setTasa(e.target.value)}
            readOnly={conDolares}
            ayuda={t(deCuentaEnDolares ? 'compra.ayudaTasaCuentaDolares' : conDolares ? 'compra.ayudaTasaDolares' : 'compra.ayudaTasa')}
          />
        )}

        {/* The parts paid some other way, above what pays the rest: read in the order they add up. */}
        <PartesDelPago
          partes={partes}
          moneda={moneda}
          cambiar={cambiarParte}
          quitar={(pagoId) => setPartes((xs) => xs.filter((y) => y.pagoId !== pagoId))}
          agregar={agregarParte}
          enfocar={enfocar}
          datos="compra"
          className="col-span-2 flex flex-col gap-3 md:col-span-4"
        />

        <div data-guia="compra-pago">
        {pagaElResto ? (
          <Selector
            etiqueta={t(partes.length > 0 ? 'compra.elResto' : 'compra.formaPago')}
            ayuda={partes.length > 0 ? t('compra.restoAyuda', { monto: enMoneda(resto as Centavos, moneda) }) : undefined}
            value={formaPago}
            onChange={(e) => setFormaPago(e.target.value as FormaPago)}
          >
            <option value="efectivo_cordobas">{t('pago.efectivo_cordobas')}</option>
            <option value="efectivo_dolares">{t('gastos.efectivoUsd')}</option>
            <option value="transferencia">{t('pago.transferencia')}</option>
            <option value="billetera">{t('gastos.billeteraMovil')}</option>
            <option value="credito">{t('compra.alCredito')}</option>
          </Selector>
        ) : (
          <p className={`text-sm ${resto < 0 ? 'text-rose-300' : 'text-slate-400'}`} data-compra-resto>
            {resto < 0 ? t('compra.partesDeMas', { monto: enMoneda(-resto as Centavos, moneda) }) : t('compra.nadaQueda')}
          </p>
        )}
        {pagaElResto && formaPago === 'transferencia' && (
          <div className="mt-3">
            <ElegirCuenta moneda={moneda} valor={cuentaPago} onCambiar={setCuentaElegida} etiqueta={t('bancos.pagaDe')} />
          </div>
        )}
        {partes.length === 0 && (
          <button type="button" className="btn-texto mt-2 gap-1.5 text-sm" onClick={agregarParte} data-compra-pagar-parte>
            <Ico icono="nuevo" className="size-4" />
            {t('compra.pagarParte')}
          </button>
        )}
        </div>

        {pagaElResto && formaPago === 'credito' && (
          <Campo
            etiqueta={t('compra.vence')}
            type="date"
            value={vencePago}
            onChange={(e) => setVencePago(e.target.value)}
          />
        )}
        {gaveta.aplica && (
          <div className="md:col-span-2">
            <SaleDeLaGaveta gaveta={gaveta} />
          </div>
        )}

        <div data-guia="compra-flete">
        <Campo
          etiqueta={t('compra.flete', { moneda: moneda === 'USD' ? 'US$' : 'C$' })}
          inputMode="decimal"
          className="tabular"
          value={flete}
          onChange={(e) => setFlete(e.target.value)}
          placeholder="0.00"
          ayuda={t('compra.ayudaFlete')}
        />
        </div>

        <div data-compra-iva-grupo>
        <Campo
          etiqueta={t('compra.iva', { moneda: moneda === 'USD' ? 'US$' : 'C$' })}
          inputMode="decimal"
          className="tabular"
          value={iva}
          onChange={(e) => setIva(e.target.value)}
          placeholder="0.00"
          ayuda={t('compra.ayudaIva')}
          error={ivaEscrito === null ? t('caja.montoInvalido') : null}
          data-compra-iva
        />
        </div>
      </section>

      <section className="tarjeta mb-4 flex flex-col gap-2">
        <Fila
          etiqueta={t('compra.subtotal')}
          valor={formatMoney(totales.sub, { symbol: moneda === 'NIO' })}
        />
        <Fila
          etiqueta={t('compra.fleteCorto')}
          valor={formatMoney(totales.flete, { symbol: moneda === 'NIO' })}
        />
        {totales.iva > 0 && (
          <Fila
            etiqueta={t('compra.ivaCorto')}
            valor={formatMoney(totales.iva, { symbol: moneda === 'NIO' })}
          />
        )}
        <div className="border-t border-slate-800 pt-2">
          <Fila
            etiqueta={t('compra.totalMoneda', { moneda: moneda === 'USD' ? 'US$' : '' }).trim()}
            valor={formatMoney(totales.total, { symbol: moneda === 'NIO' })}
            fuerte
          />
        </div>
        {moneda === 'USD' && tasaUsada > 0 && (
          <Fila etiqueta={t('compra.equivaleA')} valor={totales.enCordobas === null ? '—' : formatMoney(totales.enCordobas)} />
        )}
      </section>

      {error && <p className="mb-4 rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</p>}

      <button
        type="button"
        className="btn-primario w-full"
        data-guia="compra-registrar"
        disabled={guardando || lineas.length === 0}
        onClick={() => void guardar()}
      >
        <Ico icono="recibir" />
        {t(guardando ? 'compra.registrando' : 'compra.registrar')}
      </button>
      {revisando && <PreciosBajoMargen soloProductos={revisando} onCerrar={() => void volver({ to: '/compras' })} />}
      {/* Made here, the purchase still as it was behind the sheet, and once saved it is a line of it. */}
      {nuevo && (
        <Modal titulo={t('producto.nuevo')} onCerrar={() => setNuevo(null)}>
          <p className="mb-4 text-sm text-slate-400">{t('compra.productoNuevoAyuda')}</p>
          <FormularioProducto enHoja {...nuevo} onCreado={(c) => void creado(c)} />
        </Modal>
      )}
      {/* Made here, the purchase as it was behind the sheet, and once saved it is the supplier picked. */}
      {proveedorNuevo !== null && (
        <Modal titulo={t('prov.nuevo')} onCerrar={() => setProveedorNuevo(null)}>
          <p className="mb-4 text-sm text-slate-400">{t('compra.proveedorNuevoAyuda')}</p>
          <FormularioProveedor
            nombreInicial={proveedorNuevo}
            onCreado={(id) => {
              setProveedorId(id);
              setProveedorNuevo(null);
            }}
            onElegir={(id) => {
              setProveedorId(id);
              setProveedorNuevo(null);
            }}
          />
        </Modal>
      )}
    </Pagina>
  );
}

function Fila({ etiqueta, valor, fuerte }: { etiqueta: string; valor: string; fuerte?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={`text-sm ${fuerte ? 'font-medium' : 'text-slate-400'}`}>{etiqueta}</span>
      <span className={`tabular ${fuerte ? 'text-lg font-semibold' : 'text-sm'}`}>{valor}</span>
    </div>
  );
}
